import { useMemo } from 'react';
import * as Yup from 'yup';

import type { ResidentBalanceInfo } from '@pflegenavi/shared/api';
import { useTranslation } from 'react-i18next';
import { useFormatting } from '@pflegenavi/frontend/localization';
import type { FamilyMemberResponse } from '@pflegenavi/frontend/family-member-context';
import { useSetupDirectDebitValidationSchema } from '@pflegenavi/shared/frontend';

export interface IManualPaymentFormValues {
  amount: number;
  balance: number;
  name: string;
  email: string;
  iban: string;
}

interface UseManualPaymentValidationSchemaProps {
  familyMember: FamilyMemberResponse | undefined;
  balanceInfo: ResidentBalanceInfo | undefined;
  residentBalance: number | undefined;
}

export const useManualPaymentValidationSchema = ({
  balanceInfo,
  familyMember,
  residentBalance,
}: UseManualPaymentValidationSchemaProps): {
  initialValues: IManualPaymentFormValues;
  validationSchema: Yup.ObjectSchema<any>;
} => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();

  const topUpAmount =
    balanceInfo?.residentConfiguration?.topUpAmount !== undefined
      ? balanceInfo.residentConfiguration.topUpAmount * 100
      : undefined;

  const minAmount = balanceInfo
    ? balanceInfo.nursingHomeConfiguration.minPaymentAmount
    : 5000;
  const maxAmount = balanceInfo
    ? balanceInfo.nursingHomeConfiguration.maxPaymentAmount
    : 50000;
  const initialAmount =
    topUpAmount !== undefined && residentBalance !== undefined
      ? Math.min(maxAmount, Math.max(minAmount, topUpAmount - residentBalance))
      : minAmount;

  const initialValues: IManualPaymentFormValues = useMemo(() => {
    if (familyMember) {
      return {
        name: `${familyMember.given_name} ${familyMember.family_name}`,
        email: familyMember.email,
        amount: initialAmount,
        balance: residentBalance ? residentBalance : 0,
        iban: '',
      };
    }
    return {
      name: '',
      email: '',
      amount: initialAmount,
      balance: 0,
      iban: '',
    };
  }, [familyMember, initialAmount, residentBalance]);

  const validationSchema = Yup.object(
    useSetupDirectDebitValidationSchema()
  ).concat(
    Yup.object().shape({
      amount: Yup.number()
        .required(
          t('fm-triggered-payments.amount-screen.error-required-amount')
        )
        .min(
          minAmount,
          t('fm-triggered-payments.amount-screen.error-minimum-amount', {
            amount: fCurrencyCents(minAmount),
          })
        )
        .max(
          maxAmount,
          t('fm-triggered-payments.amount-screen.error-maximum-amount', {
            amount: fCurrencyCents(maxAmount),
          })
        ),
    })
  );

  return {
    initialValues,
    validationSchema,
  };
};
